import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || open || /^\/blogs\/.+/.test(pathname);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-[#0B132B]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="font-display font-bold text-2xl tracking-tight text-white">
            IBY<span className="text-[#FF5A36]">.</span>
          </span>
          <span className="hidden sm:block text-[10px] uppercase tracking-[0.2em] font-semibold text-white/70">
            Inspired By You
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors duration-300 ${
                  isActive ? "text-[#FF5A36]" : "text-white hover:text-[#FF5A36]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-get-involved-btn"
            className="rounded-full bg-[#FF5A36] text-white px-6 py-2.5 text-sm font-bold hover:bg-[#E04825] transition-colors duration-300"
          >
            Get Involved
          </Link>
        </nav>

        <button
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#0B132B] border-b border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  className={({ isActive }) =>
                    `font-display text-xl font-semibold ${isActive ? "text-[#FF5A36]" : "text-white"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="rounded-full bg-[#FF5A36] text-white px-6 py-3 text-center font-bold mt-2"
                data-testid="mobile-get-involved-btn"
              >
                Get Involved
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
